import * as Commons from "oc-commons/Commons";
import { ValidationMetadata } from "./ValidationMetadata";
import { getCacheStats, memoizeInplace } from "../../oc-commons/memoizeMultiMap";
import * as _ from "lodash";
import {
  following_are_optionals,
  PathFn,
  PathIntersectionFilter,
  PathIntersectionFinder,
  PProxy,
  Sep,
} from "./PathTypes";
export { following_are_optionals };

const memoizeOneArgFunction = <A, R>(f: (a: A) => R) => {
  const cache = new Map<A, R>();
  return (a: A): R => {
    let result = cache.get(a);
    if (!result) {
      result = f(a);
      cache.set(a, result);
    }
    return result;
  };
};

const pathResultProxy = <M, R>(pathFn: PathFn<M, R>, object: M | M[]) =>
  pathFn(pathProxy(object) as M) as PProxy<R>;

export const pathsAsArray =
  <M, P extends PProxy<M>, R>(pathFn: PathFn<P, R>): ((array: M[]) => R[]) =>
  (object: M[]): R[] =>
    pathResultProxy(pathFn, object as any).exitProxy() as R[];

export const pathsAsSet =
  <M, P extends PProxy<M>, R>(pathFn: PathFn<P, R>): ((array: M[]) => Set<R>) =>
  (object: M[]): Set<R> =>
    pathResultProxy(pathFn, object as any).exitProxySet() as Set<R>;

export const pathAsArray = <M, P extends PProxy<M>, R>(pathFn: PathFn<P, R>): ((array: M) => R[]) =>
  memoizeOneArgFunction(
    (object: M): R[] => pathResultProxy(pathFn, object as any).exitProxy() as R[],
  );

export const pathAsSet = <M, P extends PProxy<M>, R>(pathFn: PathFn<P, R>): ((meta: M) => Set<R>) =>
  memoizeOneArgFunction(
    (object: M): Set<R> => pathResultProxy(pathFn, object as any).exitProxySet() as Set<R>,
  );

const intersectionOfSets_ = <T>(resultSets: Set<Set<T>> | Set<T>[], optionalIndex) => {
  if (resultSets.length == 0) {
    return [];
  }
  const intersection = resultSets.reduce((result, set, index: number) => {
    const optional = optionalIndex >= 0 && index >= optionalIndex;
    const intersect = optional && set.length == 0 ? result : result.intersection(set);
    return optional && intersect.length == 0 ? result : intersect;
  });
  return sortForSortierung(intersection.toArray());
};

export const filterSinglePathIntersectionCached =
  (MAGIC_MUST_BE_UNIQUE: string) =>
  <M extends { id: number; Key: string; created_at?: Date }, R>(
    metas: M[],
    fnPathAsSet: (meta: M) => Set<R>,
  ): R[] =>
    memoizeInplace(
      () => filterSinglePathIntersection(metas, fnPathAsSet),
      MAGIC_MUST_BE_UNIQUE,
      metas,
    );

export const filterSinglePathIntersection = <
  M extends { id: number; Key: string; created_at?: Date },
  R,
>(
  metas: M[],
  fnPathAsSet: (meta: M) => Set<R>,
): R[] => {
  const resultSets = new Set(metas).map(fnPathAsSet);
  return intersectionOfSets_(resultSets, -1);
};

// export const intersectionOfSets = <T>(resultSets: (Set<T> | Sep)[]) => {
//   const optionalIndex = resultSets.findIndex((o) => o === following_are_optionals);
//   if (optionalIndex >= 0) {
//     resultSets = resultSets.toSpliced(optionalIndex, 1);
//   }
//   return intersectionOfSets_(resultSets as Set<T>[], optionalIndex);
// };

const filterPathIntersection_ = (...objectPathfunctionPairs) => {
  const optionalIndex = objectPathfunctionPairs.findIndex((o) => o === following_are_optionals);
  if (optionalIndex >= 0) {
    objectPathfunctionPairs = objectPathfunctionPairs.toSpliced(optionalIndex, 1);
  }
  const pairs = Commons.pairwise(objectPathfunctionPairs, 2, 2);
  const resultSets = pairs.map(([object, pathFunction]) =>
    pathFunction(pathProxy(object)).exitProxySet(),
  );
  return intersectionOfSets_(resultSets, optionalIndex / 2);
};

const findPathIntersection_ = (...objectPathfunctionPairs: any[]) =>
  _.first(filterPathIntersection_(...objectPathfunctionPairs));

/**
 * Erwartet als Eingabe jeweils abwechselnd ein Metadata-Objekt und eine Map-Funktion
 * und gibt alle passenden Metadata-Objekte zurück.
 * Die Map-Funktion erhält einen PathProxy des Metadata-Objekt, und muss einen
 * Path hin zur Ziel-Tabelle abbilden. Alle Paths müssen in derselben Zieltabelle enden.
 * filterPathIntersection gibt dann alle Intersections all dieser Paths zurück, wobei
 * das optionale Sortierung - Feld die Reihenfolge bestimmt.
 * Beispiel:
 * const füllungen = filterPathIntersection(
 *   this.serie, p=>elementOrTor(p.DetailSerie_Objekt).Füllung_Objekt.Füllung_Auswahlwert,
 *   this.massAuswahlwert, p=>elementOrTor(p.Mass_Auswahlwert.Mass).Füllung_Objekt.Füllung_Auswahlwert
 *   following_are_optionals,
 *   this.füllungAuswahlwert, p=>p.Füllung_Objekt
 * );
 * Tipp: Die Pfade lassen sich mit pathProxy zur Laufzeit explorieren
 */
export const filterPathIntersection = filterPathIntersection_ as PathIntersectionFilter;

/**
 * wie filterPathIntersection, gibt aber nur den ersten Treffer zurück
 */
export const findPathIntersection = findPathIntersection_ as PathIntersectionFinder;

export const filterPathIntersectionCached = (
  MAGIC_MUST_BE_UNIQUE: string,
): PathIntersectionFilter => {
  const cached_ = (...objectPathfunctionPairs) => {
    if (objectPathfunctionPairs.some((o) => Array.isArray(o))) {
      getCacheStats().callWithArrays.add(MAGIC_MUST_BE_UNIQUE);
      return filterPathIntersection_(...objectPathfunctionPairs);
    }
    const objects = objectPathfunctionPairs.filter((x) => typeof x !== "function");
    const resultCached = memoizeInplace(
      () => filterPathIntersection_(...objectPathfunctionPairs),
      MAGIC_MUST_BE_UNIQUE,
      objects,
    );
    // const result = filterPathIntersection(...objectPathfunctionPairs);
    // if (!Commons.isEqualEpsilon(result, resultCached)) {
    //   console.log("diff in filterPathIntersectionCached ", MAGIC, {
    //     result,
    //     resultCached,
    //     objectPathfunctionPairs,
    //   });
    //   debugger;
    // }
    return resultCached;
  };
  return cached_ as PathIntersectionFilter;
};

export const findPathIntersectionCached = (
  MAGIC_MUST_BE_UNIQUE: string,
): PathIntersectionFinder => {
  const cached_ = (...objectPathfunctionPairs) => {
    if (objectPathfunctionPairs.some((o) => Array.isArray(o))) {
      getCacheStats().callWithArrays.add(MAGIC_MUST_BE_UNIQUE);
      // @ts-ignore
      return findPathIntersection_(...objectPathfunctionPairs);
    }
    const objects = objectPathfunctionPairs.filter((x) => typeof x !== "function");
    const resultCached = memoizeInplace(
      // @ts-ignore
      () => findPathIntersection_(...objectPathfunctionPairs),
      MAGIC_MUST_BE_UNIQUE,
      objects,
    );

    if (location.hostname === "localhost") {
      // ----------------------------------------------------------------------
      // Das hier ist nur für Debug-Zwecke, um festzustellen, ob irgendwo das
      // caching nicht funktioniert, und macht die Performancegewinne durch den
      // Cache zunichte
      // ----------------------------------------------------------------------
      // @ts-ignore
      const result = findPathIntersection_(...objectPathfunctionPairs);
      if (!Commons.isEqualEpsilon(result, resultCached)) {
        throw new Error(`diff in findPathIntersectionCached ${MAGIC_MUST_BE_UNIQUE}`);
      }
      // ----------------------------------------------------------------------
    }

    return resultCached;
  };

  return cached_ as PathIntersectionFinder;
};

/**
 * sortiert nach dem Standardfeld Sortierung
 * @param array
 * @returns {*}
 */
export const sortForSortierung = <T extends { Sortierung?: number }>(array: T[]): T[] =>
  array.filter((a) => a != null).sort(comparatorForSortierung);
export const comparatorForSortierung = <T extends { Sortierung?: number }>(a: T, b: T): number =>
  (a?.Sortierung ?? 0) - (b?.Sortierung ?? 0);

/**
 * gibt einen Proxy zurück, mit dem man wie in einer Breitensuche durch eine Objektstruktur navigieren kann.
 * Null oder Undefined Einträge werden rausgefiltert.
 * Mit .exitProxy() verlässt man den Proxy und erhält ein Array zurück, das mittels sortForSortierung sortiert ist.
 * Mit .exitProxySet() verlässt man den Proxy und erhält einen Set zurück.
 * Mit .proxyUnion(...mappers) übergibt man eine Reihe von Path-Funktionen, auf deren Ergebnisse union angewendet wird, beispiel: .proxyUnion(p=>p.Element,p=>p.Tor).
 * Mit .proxyIntersection(...mappers) analog zu .proxyUnion.
 *
 * Beispiel: pathProxy(mein).Auto.Räder.Radmuttern.exitProxy() liefert ein Array von allen Radmuttern des Autos und ist eine
 * Kurzschreibweise für _.uniq((mein.Auto||[]).flatMap(x=>x.Räder.flatMap(x=>x.Radmuttern)).filter(x=>x!=null))
 *
 * @param objectOrArray
 */
export function pathProxy<T>(objectOrArray: T[] | T): PProxy<T> {
  const toArray = (objectOrArray: any) => {
    if (objectOrArray == null) {
      return [];
    } else if (Array.isArray(objectOrArray)) {
      return objectOrArray;
    } else if (objectOrArray instanceof Set) {
      return Array.from(objectOrArray);
    }
    return [objectOrArray];
  };
  const proxy = (array: any[]): any =>
    new Proxy(array, {
      get(array, prop: string | symbol, receiver) {
        if (prop == "exitProxy") {
          return () => sortForSortierung(_.uniq(array));
        }
        if (prop == "exitProxySet") {
          return () => new Set(array.filter((x) => x != null));
        }
        const reduceProxies =
          (reducer: any) =>
          (...pathCallbacks: any[]) => {
            const sets = pathCallbacks.map((c) => c(receiver).exitProxySet());
            if (sets.length == 0) {
              return proxy([]);
            }
            return proxy(sets.reduce(reducer).toArray());
          };
        if (prop == "proxyUnion") {
          return reduceProxies((set: any, s: any) => set.union(s));
        }
        if (prop == "proxyIntersection") {
          return reduceProxies((set: any, s: any) => set.intersection(s));
        }
        if (typeof prop === "string") {
          return proxy(
            array.flatMap((x) => {
              if (x == null) {
                return [];
              }
              // Direkt auf Property zugreifen - keine adjacency-Fallback mehr nötig
              return toArray(x[prop]);
            }),
          );
        }
        return Reflect.get(array, prop, receiver);
      },
    });
  return proxy(toArray(objectOrArray) as T[]) as PProxy<T>;
}

/**
 * Erweitert ValidationMetadata mit Pfad-basierten Navigations-Features
 */
export class GraphMetadata extends ValidationMetadata {
  constructor(metadata: any, metadataPlain: any) {
    super(metadata, metadataPlain);
  }

  async init() {
    await super.init();
    // Keine adjacency-Initialisierung mehr nötig - verwenden explizite Backreferenzen
  }
}
